import React from 'react'
import { ButtonRounded, SectionTitle } from 'is24-corecss'
import { useTranslation } from 'react-i18next'
import { IconS24ChevronDown24, IconS24ChevronUp24 } from '@is24/cosma-ui-icons'
import { RentPriceCheckDetails } from './rent-price-check-details/RentPriceCheckDetails'
import { RentPriceCheckLabel } from './rent-price-check-label/RentPriceCheckLabel'
import { PriceBenchmark } from './price-benchmark/PriceBenchmark'
import { SupplyDemandTiles } from './supply-demand-tiles/SupplyDemandTiles'
import { hasRentIndexBenchmarkData } from './price-benchmark/usePriceBenchmarkData'
import { useAppSelector } from '../../../../../../../app/hooks'
import { InitState } from '../../../../../../../redux/reducers/root/initialState'
import styles from './RentPriceCheck.module.css'

type RentPriceCheckProps = {
  price?: number
  livingSpace?: string
  room?: string
  constructionYear?: number
  rentControlApplies?: boolean | null
  savedSearches?: number
  similarObjects?: number
  isExpanded: boolean
  onExpandedChange: (isExpanded: boolean) => void
  isWebView?: boolean
}

export function RentPriceCheck({
  price,
  livingSpace,
  room,
  constructionYear,
  rentControlApplies,
  savedSearches,
  similarObjects,
  isExpanded,
  onExpandedChange,
  isWebView = false,
}: RentPriceCheckProps) {
  const { t } = useTranslation()
  const hasSupplyDemandRequiredData = Boolean(price && livingSpace?.trim() && room?.trim())
  const rentIndexResult = useAppSelector((state: InitState) => state.rentIndexFeature)
  const descriptionTranslationKey = hasRentIndexBenchmarkData(rentIndexResult)
    ? 'wizard.screen.basicInfo.price.recommendedNotification.check.descriptionRentIndex'
    : 'wizard.screen.basicInfo.price.recommendedNotification.check.description'

  return (
    <section
      className={styles.card}
      aria-label={t('wizard.screen.basicInfo.price.recommendedNotification.check.label')}
    >
      <div className={styles.title}>
        <RentPriceCheckLabel />
        <SectionTitle
          title={t('wizard.screen.basicInfo.price.recommendedNotification.check.title')}
          size='extraSmall'
          variant='bold'
          description={t(descriptionTranslationKey)}
        />
      </div>

      <PriceBenchmark price={price} showLegendInfoIcons={isWebView} />

      {!isWebView && (
        <div className={styles.details}>
          {isExpanded && (
            <RentPriceCheckDetails
              price={price}
              livingSpace={livingSpace}
              constructionYear={constructionYear}
              rentControlApplies={rentControlApplies}
            />
          )}
          {isExpanded && hasSupplyDemandRequiredData && (
            <SupplyDemandTiles savedSearches={savedSearches} similarObjects={similarObjects} />
          )}
          <ButtonRounded
            size='small'
            width='content'
            variant='textDefault'
            endElement={isExpanded ? <IconS24ChevronUp24 /> : <IconS24ChevronDown24 />}
            onClick={() => onExpandedChange(!isExpanded)}
          >
            {t(
              isExpanded
                ? 'wizard.screen.basicInfo.price.recommendedNotification.check.showLessDetails'
                : 'wizard.screen.basicInfo.price.recommendedNotification.check.showMoreDetails'
            )}
          </ButtonRounded>
        </div>
      )}
    </section>
  )
}
